import { motion, AnimatePresence } from "framer-motion";
import { WifiOff, Wifi, RefreshCw, Loader2 } from "lucide-react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useEffect, useState } from "react";

interface OfflineIndicatorProps {
  pendingChanges?: number;
  isSyncing?: boolean;
  onSync?: () => void;
}

export function OfflineIndicator({ pendingChanges = 0, isSyncing = false, onSync }: OfflineIndicatorProps) {
  const isOnline = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowReconnected(true);
      onSync?.();
      const timer = setTimeout(() => {
        setShowReconnected(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, onSync]);

  const shouldShow = !isOnline || showReconnected || isSyncing || (isOnline && pendingChanges > 0);

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          className={`fixed top-0 left-0 right-0 z-[100] md:hidden ${
            !isOnline ? 'bg-amber-500' : isSyncing ? 'bg-blue-500' : 'bg-emerald-500'
          }`}
        >
          <div className="flex items-center justify-center gap-2 py-2 px-4 text-white text-sm font-medium safe-area-top">
            {!isOnline ? (
              <>
                <WifiOff className="h-4 w-4" />
                <span>Offline • {pendingChanges} alterações pendentes</span>
              </>
            ) : isSyncing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Sincronizando dados...</span>
              </>
            ) : (
              <>
                <Wifi className="h-4 w-4" />
                <span>Conectado!</span>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
